import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  CreateUpdateServiceTemplate,
  EntityDto,
  PagedServiceTemplateRequest,
  ServiceTemplate,
  ServiceTemplateService,
} from '@services';

class ServiceTemplateStore {
  /** Current page of the My Templates table. */
  templates: Map<number, ServiceTemplate> = new Map();
  /**
   * Every template of the tenant, backing the assign-to-template selects and the Templates column
   * on My Services. Kept here rather than in each component so a change made in one place is seen
   * by all of them - the create-service drawer and the services table are siblings.
   */
  lookup: ServiceTemplate[] = [];
  lookupLoaded: boolean = false;
  lookupRequest: Promise<void> | undefined;
  template: ServiceTemplate | undefined;
  skipCount: number = 0;
  currentPage: number = 1;
  totalCount: number = 0;
  loading: boolean = false;
  saving: boolean = false;
  maxResultCount: number = 15;

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(request: PagedServiceTemplateRequest) {
    this.setLoading(true);
    try {
      const result = await ServiceTemplateService.getAll(request);
      runInAction(() => {
        this.templates.clear();
        result.items.forEach((x) => this.templates.set(x.id, x));
        this.totalCount = result.totalCount;
      });
    } catch (error) {
      console.error(error);
    }
    this.setLoading(false);
  }

  /**
   * Loads the lookup once; pass force after something changed the set of templates.
   * In-flight requests are shared, because the services table and the selects inside it all ask
   * for the lookup on the same render.
   */
  loadLookup(force: boolean = false): Promise<void> {
    if (this.lookupLoaded && !force) return Promise.resolve();
    if (this.lookupRequest) return this.lookupRequest;

    this.lookupRequest = ServiceTemplateService.getAll({ maxResultCount: 1000, skipCount: 0 })
      .then((result) => {
        runInAction(() => {
          this.lookup = result.items;
          this.lookupLoaded = true;
        });
      })
      .catch((error) => console.error(error))
      .finally(() => {
        runInAction(() => {
          this.lookupRequest = undefined;
        });
      });

    return this.lookupRequest;
  }

  async get(entityDto: EntityDto<number>) {
    this.setLoading(true);
    try {
      const result = await ServiceTemplateService.get(entityDto);
      runInAction(() => {
        this.template = result;
      });
    } catch (error) {
      console.error(error);
    }
    this.setLoading(false);
  }

  async create(request: CreateUpdateServiceTemplate) {
    this.setSaving(true);
    try {
      const result = await ServiceTemplateService.create(request);
      runInAction(() => {
        this.templates.set(result.id, result);
        this.lookup = [...this.lookup, result];
        this.totalCount += 1;
      });
      message.success({ content: 'Template created successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  async update(request: CreateUpdateServiceTemplate) {
    this.setSaving(true);
    try {
      const result = await ServiceTemplateService.update(request);
      runInAction(() => {
        this.templates.set(result.id, result);
        this.lookup = this.lookup.map((x) => (x.id === result.id ? result : x));
        this.template = result;
      });
      message.success({ content: 'Template updated successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  async delete(entityDto: EntityDto<number>) {
    this.setSaving(true);
    try {
      await ServiceTemplateService.delete(entityDto);
      runInAction(() => {
        this.templates.delete(entityDto.id);
        this.lookup = this.lookup.filter((x) => x.id !== entityDto.id);
        this.totalCount -= 1;
      });
      message.success({ content: 'Template deleted successfully!' });
    } catch (error) {
      console.error(error);
    } finally {
      this.setSaving(false);
    }
  }

  /**
   * Replaces the templates one service belongs to - used from the My Services page.
   * Reloads the lookup because this moves a service across several templates at once, which the
   * returned slice alone cannot describe.
   */
  async setServiceTemplates(serviceId: number, templateIds: number[]) {
    this.setSaving(true);
    try {
      const result = await ServiceTemplateService.setServiceTemplates(serviceId, templateIds);
      await this.loadLookup(true);
      message.success({ content: 'Templates updated successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSaving = (value: boolean) => {
    runInAction(() => {
      this.saving = value;
    });
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };
}

export default ServiceTemplateStore;
